import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AccountPage.css';
import NavBarV2 from '../components/NavBarV2';
import Footer from "../components/Footer";


const AccountPage = () => {
  const [account, setAccount] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProgress, setUserProgress] = useState([]);
  const [userAchievements, setUserAchievements] = useState([]);
  const [achievements, setAchievements] = useState({
    attemptedBeginner: 0,
    completedBeginner: 0,
    attemptedIntermediate: 0,
    completedIntermediate: 0,
    attemptedPro: 0,
    completedPro: 0,
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordChangeMessage, setPasswordChangeMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const { id } = useParams();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAccountAndProgress = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      if (!token) {
        setIsLoading(false);
        navigate('/');
        return;
      }
      try {
        const response = await axios.get(`${baseUrl}/users/account/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response) {
          throw new Error('Failed to fetch account details');
        }
        const accountInfo = response.data;

        // Set user achievements if they exist in the response data
        if (accountInfo.achievements && accountInfo.achievements.length > 0) {
          setUserAchievements(accountInfo.achievements);
        }

        try {
          const progressResponse = await axios.get(`${baseUrl}/progress/${userId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (!progressResponse) {
            throw new Error('Failed to fetch user progress');
          }
          const progressData = progressResponse.data;
          setUserProgress(progressData);
          const progressAchievements = calculateAchievementStats(progressData);
          setAchievements(progressAchievements);
        } catch (progressError) {
          console.error(progressError);
        }
        setAccount(accountInfo);
        setIsLoading(false);
      } catch (error) {
        setError(error);
        setIsLoading(false);
        console.error(error);
      }
    };
    fetchAccountAndProgress();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      const response = await axios.put(
        `${baseUrl}/users/account/change-password`,
        { currentPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPasswordChangeMessage(response.data.message);
      setMessageType('success');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordChangeMessage('Failed to change password.');
      setMessageType('error');
    }
  };

  const togglePasswordReset = () => {
    setIsPasswordResetOpen(!isPasswordResetOpen);
  };

  const getInitials = (username) => {
    if (!username) return '?';
    return username.charAt(0).toUpperCase();
  };

  // Helper function to calculate achievement stats
  const calculateAchievementStats = (progressData) => {
    if (!progressData || progressData.length === 0) {
      return {
        attemptedBeginner: 0,
        completedBeginner: 0,
        attemptedIntermediate: 0,
        completedIntermediate: 0,
        attemptedPro: 0,
        completedPro: 0,
      };
    }
    const stats = {
      attemptedBeginner: 0,
      completedBeginner: 0,
      attemptedIntermediate: 0,
      completedIntermediate: 0,
      attemptedPro: 0,
      completedPro: 0,
    };
    progressData.forEach((trackedProblem) => {
      if (trackedProblem.level === 'BEGINNER') {
        stats.attemptedBeginner += trackedProblem.attempts;
        if (trackedProblem.completed) {
          stats.completedBeginner += 1;
        }
      } else if (trackedProblem.level === 'INTERMEDIATE') {
        stats.attemptedIntermediate += trackedProblem.attempts;
        if (trackedProblem.completed) {
          stats.completedIntermediate += 1;
        }
      } else if (trackedProblem.level === 'PRO') {
        stats.attemptedPro += trackedProblem.attempts;
        if (trackedProblem.completed) {
          stats.completedPro += 1;
        }
      }
    });
    return stats;
  };

  if (!account) {
    return (
      <div className="no-account-container">
        <p>You are not logged in. Please log in or create an account.</p>
      </div>
    );
  }

  return (
    <div className="account-page-main">
      <div className="account-page-container">
        <h2 className="account-header">Your Account</h2>

        <div className="account-content">
          {/* Profile Sidebar */}
          <div className="profile-sidebar">
            <h3>Profile</h3>
            <div className="profile-avatar">{getInitials(account.username)}</div>
            <div className="profile-details">
              <div className="profile-item">
                <span className="profile-label">Username</span>
                <span className="profile-value">
                  {account.username || `BlockCode User ${account.id}`}
                </span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Email</span>
                <span className="profile-value">{account.email}</span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Membership</span>
                <span className="profile-value">{account.role || 'Basic'}</span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Member Since</span>
                <span className="profile-value">
                  {new Date(account.createdAt || Date.now()).toLocaleDateString()}
                </span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Current XP</span>
                <span className="profile-value">{account.xp || 0} XP</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="main-content">
            {/* Achievements Section */}
            <div className="achievements-section">
              <h3>Achievements</h3>
              <div className="achievements-grid">
                <div className="achievement-card">
                  <div className="achievement-label">Attempted 5 Beginner Problems</div>
                  <div className="progress-container">
                    <progress value={achievements.attemptedBeginner} max="5"></progress>
                    <span className="progress-text">{achievements.attemptedBeginner}/5</span>
                  </div>
                </div>

                <div className="achievement-card">
                  <div className="achievement-label">Completed 5 Beginner Problems</div>
                  <div className="progress-container">
                    <progress value={achievements.completedBeginner} max="5"></progress>
                    <span className="progress-text">{achievements.completedBeginner}/5</span>
                  </div>
                </div>

                <div className="achievement-card">
                  <div className="achievement-label">Attempted 5 Pro Problems</div>
                  <div className="progress-container">
                    <progress value={achievements.attemptedPro} max="5"></progress>
                    <span className="progress-text">{achievements.attemptedPro}/5</span>
                  </div>
                </div>
              </div>

              {/* Display earned achievements */}
              {userAchievements && userAchievements.length > 0 && (
                <div className="earned-achievements">
                  <h4>Earned Achievements</h4>
                  <div className="earned-achievements-grid">
                    {userAchievements.map((userAchievement) => (
                      <div key={userAchievement.id} className="earned-achievement-card">
                        <div className="achievement-title">{userAchievement.achievement.name}</div>
                        <div className="achievement-description">
                          {userAchievement.achievement.description}
                        </div>
                        <div className="achievement-xp">
                          +{userAchievement.achievement.xpReward} XP
                        </div>
                        <div className="achievement-earned-date">
                          Earned on {new Date(userAchievement.earnedAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Progress Section */}
            <div className="progress-section">
              <h3>User Progress</h3>
              {userProgress?.length > 0 ? (
                <table className="progress-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Level</th>
                      <th>Attempts</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userProgress.map((trackedProblem) => (
                      <tr key={trackedProblem.id}>
                        <td data-label="Title">{trackedProblem.problem.title}</td>
                        <td data-label="Level">{trackedProblem.level}</td>
                        <td data-label="Attempts">{trackedProblem.attempts}</td>
                        <td data-label="Status">
                          <Link
                            to={`/problems/${trackedProblem.problem.id}`}
                            className={`progress-status-link ${
                              trackedProblem.completed ? 'completed' : 'in-progress'
                            }`}
                          >
                            {trackedProblem.completed ? 'Completed' : 'In Progress'}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="progress-empty">You haven't started any problems yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Collapsible Password Reset Section */}
        <div className="password-reset-container">
          <div className="password-reset-header" onClick={togglePasswordReset}>
            <h3>Change Password</h3>
            <span className={`password-reset-toggle ${isPasswordResetOpen ? 'open' : ''}`}>▼</span>
          </div>

          <div className={`password-reset-content ${isPasswordResetOpen ? 'open' : ''}`}>
            <form className="password-reset-form" onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label htmlFor="current-password">Current Password</label>
                <input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="password-reset-button">
                Update Password
              </button>
            </form>

            {passwordChangeMessage && (
              <p className={`password-change-message ${messageType}`}>{passwordChangeMessage}</p>
            )}
          </div>
        </div>
        <Footer />

      </div>
    </div>
  );
};

export default AccountPage;
